import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { StripeEmbeddedCheckout, loadStripe } from '@stripe/stripe-js';
import { CartService } from '../../core/services/cart.service';
import { CheckoutService } from '../../core/services/checkout.service';
import { OrderService } from '../../core/services/order.service';

@Component({
  selector: 'app-checkout-pay',
  imports: [RouterLink],
  templateUrl: './checkout-pay.component.html'
})
export class CheckoutPayComponent implements OnInit, OnDestroy {
  @ViewChild('checkoutMount', { static: true }) checkoutMount!: ElementRef<HTMLDivElement>;

  private readonly cart = inject(CartService);
  private readonly checkout = inject(CheckoutService);
  private readonly orders = inject(OrderService);
  private readonly router = inject(Router);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);

  private embedded: StripeEmbeddedCheckout | null = null;
  private sessionId: string | null = null;
  private completing = false;

  async ngOnInit(): Promise<void> {
    const state = this.checkout.readEmbeddedSession();
    if (!state?.clientSecret || !state.publishableKey || !state.sessionId) {
      this.loading.set(false);
      this.error.set('Missing checkout session. Return to checkout and try again.');
      return;
    }

    this.sessionId = state.sessionId;

    try {
      const stripe = await loadStripe(state.publishableKey);
      if (!stripe) {
        throw new Error('Stripe.js failed to load.');
      }

      this.embedded = await stripe.createEmbeddedCheckoutPage({
        fetchClientSecret: async () => state.clientSecret,
        onComplete: () => this.completeCheckout()
      });

      this.embedded.mount(this.checkoutMount.nativeElement);
      this.loading.set(false);
    } catch (err) {
      this.loading.set(false);
      const message = err instanceof Error ? err.message : null;
      this.error.set(message ?? 'Could not start checkout. Please try again.');
    }
  }

  cancel(): void {
    const id = this.sessionId;
    this.embedded?.destroy();
    this.embedded = null;
    this.checkout.clearEmbeddedSession();

    if (!id) {
      void this.router.navigateByUrl('/checkout');
      return;
    }

    this.checkout.cancelSession(id).subscribe({
      next: () => void this.router.navigateByUrl('/checkout'),
      error: () => void this.router.navigateByUrl('/checkout')
    });
  }

  ngOnDestroy(): void {
    this.embedded?.destroy();
    this.embedded = null;
  }

  private completeCheckout(): void {
    if (this.completing) {
      return;
    }
    this.completing = true;

    const sessionId = this.sessionId;
    this.embedded?.destroy();
    this.embedded = null;

    if (!sessionId) {
      void this.router.navigateByUrl('/checkout');
      return;
    }

    // Drop cart immediately so the badge resets even if receipt/redirect races.
    this.cart.clear();

    this.loading.set(true);
    this.orders.getReceiptBySession(sessionId).subscribe({
      next: () => this.goToSuccess(sessionId),
      error: () => this.goToSuccess(sessionId)
    });
  }

  private goToSuccess(sessionId: string): void {
    void this.router.navigate(['/checkout/success'], {
      queryParams: { session_id: sessionId }
    });
  }
}
